import { AxiosInstance, AxiosRequestConfig } from 'axios';

import {
  Order,
  OrderAttributeDefinition,
  OrderDetail,
  OrderStatus,
} from './models/order';

const ENDPOINT = 'order/';

export type OrderListPayload = {
  limit: number;
  offset: number;
  sort: string;
  filter?: {
    distributor_id?: number;
    outlet_id?: number;
    status?: string;
    created_from?: string;
    created_to?: string;
    username?: string;
  };
};

export type OrderItem = {
  sku_id: number;
  number: number;
  unit_sku_id?: number;
  price: number;
};

export type OrderAttributeValue = {
  attribute_id: number;
  value: string;
};

export type OrderVisitCreatePayload = {
  visit_id: number;
  outlet_id: number;
  distributor_id: number;
  warehouse_id: number;
  price_list_id: number;
  shipping_date: string;
  comment?: string;
  items: OrderItem[];
  attributes?: OrderAttributeValue[];
};

export type OrderUpdatePayload = {
  visit_id?: number;
  outlet_id?: number;
  distributor_id?: number;
  warehouse_id?: number;
  price_list_id?: number;
  shipping_date?: string;
  comment?: string;
  status_id?: number;
  items?: OrderItem[];
  attributes?: OrderAttributeValue[];
};

export type OrderAttributeDefinitionCreatePayload = {
  name: string;
  code: string;
  distributor_id?: number | null;
  data_type: string;
  options?: Record<string, any> | null;
  is_required?: boolean;
  is_multiselect?: boolean;
  line_numbers?: number | null;
  max_length?: number | null;
  reg_exp?: string | null;
  mask?: string;
  conditional_rendering_rules?: Record<string, any> | null;
};

export type OrderAttributeDefinitionListPayload = {
  limit: number;
  offset: number;
  sort: string;
  filter?: {
    distributor_id?: number | null;
    code?: string;
    name?: string;
  };
};

export type OrderAttributeDefinitionUpdatePayload = {
  name?: string;
  data_type?: string;
  options?: Record<string, any> | null;
};

export type OrderStatusListPayload = {
  limit: number;
  offset: number;
  sort: string;
  filter?: {
    id?: number;
    name?: string;
    is_active?: boolean;
  };
};

// eslint-disable-next-line import/no-anonymous-default-export
export default (instance: AxiosInstance, config: AxiosRequestConfig) => ({
  // ORDERS
  getOrders: (payload: OrderListPayload) =>
    instance.post<ApiResponse<Paginated<'data', Order>>>(ENDPOINT + 'list/', payload, config),
  getOrder: (orderId: number | string) =>
    instance.get<ApiResponse<OrderDetail>>(ENDPOINT + `list/${orderId}/`, config),
  createVisitOrder: (payload: OrderVisitCreatePayload) =>
    instance.post<ApiResponse<any>>(ENDPOINT + 'visit/', payload, config),
  updateOrder: (orderId: number | string, payload: OrderUpdatePayload) =>
    instance.patch<ApiResponse<any>>(ENDPOINT + `${orderId}/`, payload, config),

  // ATTRIBUTE DEFINITIONS
  getAttributes: (payload: OrderAttributeDefinitionListPayload) =>
    instance.post<ApiResponse<Paginated<'data', OrderAttributeDefinition>>>(
      ENDPOINT + 'attributes/list/', payload, config),
  createAttribute: (payload: OrderAttributeDefinitionCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'attributes/', payload, config),
  updateAttribute: (pid: number | string, payload: OrderAttributeDefinitionUpdatePayload) =>
    instance.patch<ApiResponse<{ id: number }>>(ENDPOINT + `attributes/${pid}/`, payload, config),

  // STATUSES
  getStatuses: (payload: OrderStatusListPayload) =>
    instance.post<ApiResponse<Paginated<'data', OrderStatus>>>(ENDPOINT + 'statuses/list', payload, config),
  createStatus: (payload: { name: string }) =>
    instance.post<ApiResponse<OrderStatus>>(ENDPOINT + 'statuses/create', payload, config),
  updateStatus: (pid: number | string, payload: { name: string; is_active: boolean }) =>
    instance.patch<ApiResponse<OrderStatus>>(ENDPOINT + `statuses/update/${pid}`, payload, config),
});
